import { useState } from 'react';
import { useIntl } from 'react-intl';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { PATHS } from '../../config/consts';
import { buildUrlWithQueryParams, UrlQueryParamName, UrlQueryParamValueBoolean } from '../../helpers/queryParams';
import { useLocationState } from '../../hooks/useLocationState';
import actions from '../../modules/auth';
import { setOryLoginStatus } from '../../modules/auth/actions';
import { TYPE_DESCRIPTOR } from '../../modules/notifications';
import { addSnackbarError } from '../../modules/notifications/actions';
import { getCurrentUser } from '../../modules/user/actions';
import { containsOneOfSpecificErrors } from '../../ory/errors/errorParser';
import { useOryLoginApi, useOryRecoveryApi } from '../../ory/hooks/oryApi';
import { OryResponseParserResult, useOryResponseParser } from '../../ory/hooks/oryResponseParser';
import { IOryRecoveryFlow, OryErrorNamedMessageId, OryLoginStatus, OryResponseWithData } from '../../ory/types';
import { getSsoOrganizationMap, tryToGetSSOOrganizationIdFromEmailValue } from '../../ory/utils/ssoOrganizations';
import { ILogoutLocationStateRedirect } from '../LogoutPage/LogoutPage';
import messages from './messages';
import { getResetPasswordPageTitle } from './title';
import { IResetPasswordFormData, ResetPasswordFormStep } from './types';

export interface IUseResetPasswordPage {
  recoveryWholeProcessRestart: () => void;
  recoveryFlowRestart: (newFlowId?: string) => Promise<boolean>;
  emailFormSubmit: (values: IResetPasswordFormData) => void;
  recoveryCodeFormSuccess: () => void;
  newPasswordFormSuccess: () => void;
  resetPasswordFormStep: ResetPasswordFormStep;
  setResetPasswordFormStep: React.Dispatch<React.SetStateAction<ResetPasswordFormStep>>;
  email: string;
  isOryApiFetching: boolean;
  oryRecoveryFlowData: IOryRecoveryFlow | undefined;
  formTitle: string;
}

export function useResetPasswordPage(): IUseResetPasswordPage {
  const dispatch = useDispatch();
  const { formatMessage } = useIntl();
  const [resetPasswordFormStep, setResetPasswordFormStep] = useState(ResetPasswordFormStep.EmailForm);
  const [oryRecoveryFlowData, setOryRecoveryFlowData] = useState<IOryRecoveryFlow | undefined>(undefined);
  const { parseOryResponseWithData } = useOryResponseParser();
  const { createOrGetOryRecoveryFlow, isOryApiFetching } = useOryRecoveryApi();
  const { navigateWithState } = useLocationState<ILogoutLocationStateRedirect>();
  const { getOryCurrentUserSession } = useOryLoginApi();
  const [email, setEmail] = useState('');
  const navigate = useNavigate();

  const recoveryFlowRestart = async (newFlowId?: string): Promise<boolean> => {
    const response = await createOrGetOryRecoveryFlow({ email, existingFlowId: newFlowId });
    return processCreateOrGetOryRecoverFlowResponse(response);
  };

  const recoveryWholeProcessRestart = () => {
    setOryRecoveryFlowData(undefined);
    setResetPasswordFormStep(ResetPasswordFormStep.EmailForm);
    setEmail(email);
  };

  const processCreateOrGetOryRecoverFlowResponse = (
    response: OryResponseWithData<IOryRecoveryFlow>
  ): OryResponseParserResult => {
    return parseOryResponseWithData({
      onError: (error) => {
        if (error && containsOneOfSpecificErrors(error, [OryErrorNamedMessageId.Session_already_available])) {
          const navigatePath = buildUrlWithQueryParams({
            baseUrl: PATHS.LOGOUT,
            queryParams: { [UrlQueryParamName.IsOrySessionActive]: UrlQueryParamValueBoolean.True },
          });
          navigateWithState({
            navigateTo: navigatePath,
            state: { redirectTo: PATHS.PASSWORD_RESET },
          });
        }
      },
      onSuccess: (data) => {
        setOryRecoveryFlowData(data);
        setResetPasswordFormStep(ResetPasswordFormStep.ORYRecoveryCodeForm);
      },
      response,
    });
  };

  const recoveryCodeFormSuccess = async () => {
    const response = await getOryCurrentUserSession();
    parseOryResponseWithData({
      onError: (error) => {
        if (error && containsOneOfSpecificErrors(error, [OryErrorNamedMessageId.Session_aal2_required])) {
          setResetPasswordFormStep(ResetPasswordFormStep.MFAForm);
        }
      },
      onSuccess: () => {
        dispatch(setOryLoginStatus(OryLoginStatus.SessionConfirmed));
        dispatch(getCurrentUser());
        setResetPasswordFormStep(ResetPasswordFormStep.ORYNewPasswordForm);
      },
      response,
    });
  };

  const newPasswordFormSuccess = () => {
    navigate(PATHS.LOGIN);
  };

  const emailFormSubmit = async (values: IResetPasswordFormData) => {
    if (values.email && (await tryToGetSSOOrganizationIdFromEmailValue(values.email, getSsoOrganizationMap()))) {
      dispatch(addSnackbarError(messages.ImsResetPasswordErrorSSO, TYPE_DESCRIPTOR));
      return;
    }

    const onUserIsInOry = async () => {
      const response = await createOrGetOryRecoveryFlow({ email: values.email });
      processCreateOrGetOryRecoverFlowResponse(response);
    };
    setEmail(values.email);
    dispatch(actions.resetPasswordRequest({ email: values.email, onUserIsInOry }));
  };

  return {
    email,
    emailFormSubmit,
    formTitle: getResetPasswordPageTitle(resetPasswordFormStep, formatMessage),
    isOryApiFetching,
    newPasswordFormSuccess,
    oryRecoveryFlowData,
    recoveryCodeFormSuccess,
    recoveryFlowRestart,
    recoveryWholeProcessRestart,
    resetPasswordFormStep,
    setResetPasswordFormStep,
  };
}
